import { useRef } from "react";

/**
 * useSwipe — hook léger pour détecter les gestes de swipe horizontaux
 * sur un élément (touch + pointer). Renvoie un objet {onTouchStart, onTouchMove, onTouchEnd}
 * à spread sur ta div/carte.
 *
 * Usage :
 *   const handlers = useSwipe({
 *     onSwipeLeft: () => markStatus("refused"),
 *     onSwipeRight: () => markStatus("callback"),
 *     threshold: 80,
 *   });
 *   <div {...handlers}>...</div>
 *
 * Un swipe VERTICAL (défilement de la page) ne déclenchera JAMAIS de callback,
 * on ne réagit qu'aux gestes clairement horizontaux (>2× le mouvement Y).
 */
export function useSwipe({ onSwipeLeft, onSwipeRight, threshold = 70, onSwipeProgress } = {}) {
  const start = useRef({ x: 0, y: 0, t: 0 });
  const active = useRef(false);

  const onTouchStart = (e) => {
    const t = e.touches?.[0] || e;
    start.current = { x: t.clientX, y: t.clientY, t: Date.now() };
    active.current = true;
    onSwipeProgress?.(0);
  };

  const onTouchMove = (e) => {
    if (!active.current) return;
    const t = e.touches?.[0] || e;
    const dx = t.clientX - start.current.x;
    const dy = t.clientY - start.current.y;
    // Si le geste devient plutôt vertical, on annule
    if (Math.abs(dy) > Math.abs(dx) * 1.2 && Math.abs(dy) > 20) {
      active.current = false;
      onSwipeProgress?.(0);
      return;
    }
    onSwipeProgress?.(dx);
  };

  const onTouchEnd = (e) => {
    if (!active.current) { onSwipeProgress?.(0); return; }
    active.current = false;
    const t = e.changedTouches?.[0] || e;
    const dx = t.clientX - start.current.x;
    const dy = t.clientY - start.current.y;
    const dt = Date.now() - start.current.t;
    onSwipeProgress?.(0);
    // Distance minimale + geste horizontal net + durée raisonnable
    if (Math.abs(dx) >= threshold && Math.abs(dx) > Math.abs(dy) * 1.5 && dt < 800) {
      if (dx < 0) onSwipeLeft?.();
      else onSwipeRight?.();
    }
  };

  const onTouchCancel = () => { active.current = false; onSwipeProgress?.(0); };

  return { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel };
}

export default useSwipe;
